import { useMemo, useState } from 'react';
import { X, Mail, Loader2, User, Building2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { EntityType } from '@peles/shared';
import { useEntityLinks } from '../../hooks/useEntityLinks';
import { useUsers } from '../../hooks/useUsers';
import * as eventsApi from '../../api/events';

interface SendVendorListModalProps {
  eventId: string;
  eventName: string;
  eventDate: string;
  venueId: string | null | undefined;
  currentUser: { email: string; name: string };
  onClose: () => void;
}

export function SendVendorListModal({
  eventId,
  eventName,
  eventDate,
  venueId,
  currentUser,
  onClose,
}: SendVendorListModalProps) {
  const [selectedContactEmails, setSelectedContactEmails] = useState<string[]>([]);
  const [includeSelf, setIncludeSelf] = useState(true);

  const { data: contactLinks = [], isLoading: loadingContacts } = useEntityLinks({
    sourceEntityType: EntityType.VENUE,
    sourceEntityId: venueId ?? '',
    linkType: 'CONTACT',
  });
  const { data: users = [] } = useUsers({ isActive: true });

  const venueContacts = useMemo(() => {
    const userMap = new Map(users.map((u) => [u.id, u]));
    return contactLinks
      .map((link) => {
        const user = userMap.get(link.targetEntityId);
        const meta = (link.metadata || {}) as Record<string, unknown>;
        return {
          id: link.id,
          userName: link.targetEntityName || user?.name || 'Unknown',
          userEmail: user?.email || '',
          title: (meta.title as string) || '',
        };
      })
      .filter((c) => !!c.userEmail);
  }, [contactLinks, users]);

  const sendMutation = useMutation({
    mutationFn: (recipientEmails: string[]) =>
      eventsApi.sendVendorListEmail(eventId, {
        recipientEmails,
        replyTo: currentUser.email,
      }),
    onSuccess: onClose,
  });

  const toggleContact = (email: string) => {
    setSelectedContactEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
  };

  const handleSend = () => {
    const recipients = new Set<string>(selectedContactEmails);
    if (includeSelf) recipients.add(currentUser.email);
    if (recipients.size === 0) return;
    sendMutation.mutate(Array.from(recipients));
  };

  const recipientCount = new Set([
    ...selectedContactEmails,
    ...(includeSelf ? [currentUser.email] : []),
  ]).size;

  const subject = `Vendor list for ${eventName} - ${eventDate}`;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh] rounded-t-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-primary-600" />
            <h2 className="text-base font-semibold text-gray-900">Send Vendor List</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Email subject preview */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
              Subject
            </p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 break-words">
              {subject}
            </p>
          </div>

          {/* Send to myself */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Sender</p>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={includeSelf}
                onChange={(e) => setIncludeSelf(e.target.checked)}
                className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
              />
              <User size={15} className="text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{currentUser.name}</p>
                <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
              </div>
              <span className="ml-auto text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full flex-shrink-0">
                You
              </span>
            </label>
          </div>

          {/* Venue contacts */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
              Venue Contacts
              {venueContacts.length > 0 && (
                <span className="ml-1 text-gray-400 normal-case">({venueContacts.length})</span>
              )}
            </p>

            {!venueId ? (
              <p className="text-sm text-gray-400 italic px-1">No venue linked to this event.</p>
            ) : loadingContacts ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                <Loader2 size={14} className="animate-spin" />
                Loading contacts…
              </div>
            ) : venueContacts.length === 0 ? (
              <p className="text-sm text-gray-400 italic px-1">No contacts found for this venue.</p>
            ) : (
              <div className="space-y-2">
                {venueContacts.map((contact) => (
                  <label
                    key={contact.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedContactEmails.includes(contact.userEmail)}
                      onChange={() => toggleContact(contact.userEmail)}
                      className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                    />
                    <Building2 size={15} className="text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{contact.userName}</p>
                      <p className="text-xs text-gray-500 truncate">{contact.userEmail}</p>
                    </div>
                    {contact.title && (
                      <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                        {contact.title}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          {sendMutation.isError && (
            <p className="text-xs text-red-600 flex-1">
              {sendMutation.error instanceof Error
                ? sendMutation.error.message
                : 'Failed to send email'}
            </p>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={recipientCount === 0 || sendMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {sendMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              Send to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
